import { useNavigate, useSearch } from "@tanstack/react-router";
import { deepEqual } from "fast-equals";
import { useCallback, useLayoutEffect, useMemo, useState, type PropsWithChildren } from "react";

import { useAuth } from "@/api/resources/auth/hooks";
import { MaintenanceViewStatusType } from "@/components/Maintenance/types";
import { getDefaultMaintenanceHistoryDateRange } from "@/components/Maintenance/utils";
import { defaultGlobalFilters, GlobalFiltersContext, type FilterOptions } from "@/providers/globalFiltersConfig";
import { makeQueryFilterIn, type QueryFilter } from "@/utils/filter";
import { trackPosthogEvent } from "@/utils/posthog";

import type { CaseCustomFilter } from "@/api/resources/cases/types";
import type { MaintenanceCustomFilter } from "@/api/resources/maintenances/types";
import type { CaseStatus, CaseType, MaintenanceStatus } from "@/supabase.types";

type QueryFilters = {
  [
    key in keyof Omit<
      FilterOptions,
      | "caseCustomFilter"
      | "maintenanceCustomFilter"
      | "mDateFrom"
      | "mDateUntil"
      | "mDate"
      | "viewDaily"
      | "viewServiceJobs"
      | "viewServiceJobsGantt"
      | "viewCases"
      | "viewCasesStatus"
      | "viewMaintenances"
      | "viewMaintenancesStatus"
      | "viewPartPicking"
      | "viewPartConstraints"
    >
  ]: QueryFilter | undefined;
};

const GlobalFiltersProvider = ({ children }: PropsWithChildren) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const filterOptions = useSearch({
    strict: false,
    select: ({
      deployments,
      users,
      manufacturers,
      deviceModels,
      devices,
      maintenanceStatus,
      deviceTags,
      caseTags,
      caseTypes,
      caseStatus,
      caseCustomFilter,
      maintenanceTags,
      maintenanceCustomFilter,
      mDateFrom,
      mDateUntil,
      mDate,
      viewDaily,
      viewServiceJobs,
      viewServiceJobsGantt,
      viewCases,
      viewCasesStatus,
      viewMaintenances,
      viewMaintenancesStatus,
      viewPartPicking,
      viewPartConstraints,
    }) => ({
      deployments,
      users,
      manufacturers,
      deviceModels,
      devices,
      maintenanceStatus: maintenanceStatus as MaintenanceStatus[] | undefined,
      deviceTags,
      caseTags,
      caseTypes: caseTypes as CaseType[] | undefined,
      caseStatus: caseStatus as CaseStatus[] | undefined,
      caseCustomFilter: caseCustomFilter as CaseCustomFilter | undefined,
      maintenanceTags,
      maintenanceCustomFilter: maintenanceCustomFilter as MaintenanceCustomFilter | undefined,
      mDateFrom,
      mDateUntil,
      mDate,
      viewDaily,
      viewServiceJobs,
      viewServiceJobsGantt,
      viewCases,
      viewCasesStatus,
      viewMaintenances,
      viewMaintenancesStatus,
      viewPartPicking,
      viewPartConstraints,
    }),
  });

  const [globalFilterOptions, setGlobalFilterOptions] = useState<FilterOptions>(() => ({
    deployments: filterOptions.deployments ?? defaultGlobalFilters.deployments,
    users: filterOptions.users ?? defaultGlobalFilters.users,
    manufacturers: filterOptions.manufacturers ?? defaultGlobalFilters.manufacturers,
    deviceModels: filterOptions.deviceModels ?? defaultGlobalFilters.deviceModels,
    devices: filterOptions.devices ?? defaultGlobalFilters.devices,
    maintenanceStatus: filterOptions.maintenanceStatus ?? defaultGlobalFilters.maintenanceStatus,
    deviceTags: filterOptions.deviceTags ?? defaultGlobalFilters.deviceTags,
    caseTags: filterOptions.caseTags ?? defaultGlobalFilters.caseTags,
    caseTypes: filterOptions.caseTypes ?? defaultGlobalFilters.caseTypes,
    caseStatus: filterOptions.caseStatus ?? defaultGlobalFilters.caseStatus,
    caseCustomFilter: filterOptions.caseCustomFilter ?? defaultGlobalFilters.caseCustomFilter,
    maintenanceTags: filterOptions.maintenanceTags ?? defaultGlobalFilters.maintenanceTags,
    maintenanceCustomFilter: filterOptions.maintenanceCustomFilter ?? defaultGlobalFilters.maintenanceCustomFilter,
    mDateFrom: filterOptions.mDateFrom ?? defaultGlobalFilters.mDateFrom,
    mDateUntil: filterOptions.mDateUntil ?? defaultGlobalFilters.mDateUntil,
    mDate: filterOptions.mDate ?? defaultGlobalFilters.mDate,
    viewDaily: filterOptions.viewDaily ?? defaultGlobalFilters.viewDaily,
    viewServiceJobs: filterOptions.viewServiceJobs ?? defaultGlobalFilters.viewServiceJobs,
    viewServiceJobsGantt: filterOptions.viewServiceJobsGantt ?? defaultGlobalFilters.viewServiceJobsGantt,
    viewCases: filterOptions.viewCases ?? defaultGlobalFilters.viewCases,
    viewCasesStatus: filterOptions.viewCasesStatus ?? defaultGlobalFilters.viewCasesStatus,
    viewMaintenances: filterOptions.viewMaintenances ?? defaultGlobalFilters.viewMaintenances,
    viewMaintenancesStatus: filterOptions.viewMaintenancesStatus ?? defaultGlobalFilters.viewMaintenancesStatus,
    viewPartPicking: filterOptions.viewPartPicking ?? defaultGlobalFilters.viewPartPicking,
    viewPartConstraints: filterOptions.viewPartConstraints ?? defaultGlobalFilters.viewPartConstraints,
  }));

  const trackableSetGlobalFilterOptions = useCallback((params: Parameters<typeof setGlobalFilterOptions>[0]) => {
    trackPosthogEvent(`set`, `global filters | filterbar`, {
      values: params,
    });
    setGlobalFilterOptions(params);
  }, []);

  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback(() => {
    trackPosthogEvent(`toggle`, `global filters | filterbar`);
    setExpanded((_expanded) => !_expanded);
  }, []);
  const close = useCallback(() => {
    trackPosthogEvent(`close`, `global filters | filterbar`);
    setExpanded(false);
  }, []);
  const resetOptions = useCallback(() => {
    trackPosthogEvent(`reset`, `global filters | filterbar`);
    close();
    // defaultGlobalFilters.mDateFrom/mDateUntil are the Upcoming view's default — if the preserved
    // view is History, that default is a future range the resolvedDate filter never matches, so
    // re-derive it the same way MaintenanceDataPage's setSafeDateHistory does
    const [mDateFrom, mDateUntil] =
      globalFilterOptions.viewMaintenancesStatus === MaintenanceViewStatusType.History
        ? getDefaultMaintenanceHistoryDateRange()
        : [defaultGlobalFilters.mDateFrom, defaultGlobalFilters.mDateUntil];
    // reset all globalFilters except views, to not change view due to a reset
    const _defaultGlobalFilters: FilterOptions = {
      ...defaultGlobalFilters,
      mDateFrom,
      mDateUntil,
      viewDaily: globalFilterOptions.viewDaily,
      viewServiceJobs: globalFilterOptions.viewServiceJobs,
      viewServiceJobsGantt: globalFilterOptions.viewServiceJobsGantt,
      viewCases: globalFilterOptions.viewCases,
      viewCasesStatus: globalFilterOptions.viewCasesStatus,
      viewMaintenances: globalFilterOptions.viewMaintenances,
      viewMaintenancesStatus: globalFilterOptions.viewMaintenancesStatus,
      viewPartPicking: globalFilterOptions.viewPartPicking,
      viewPartConstraints: globalFilterOptions.viewPartConstraints,
    };
    trackableSetGlobalFilterOptions(_defaultGlobalFilters);
  }, [
    globalFilterOptions.viewDaily,
    globalFilterOptions.viewServiceJobs,
    globalFilterOptions.viewServiceJobsGantt,
    globalFilterOptions.viewCases,
    globalFilterOptions.viewCasesStatus,
    globalFilterOptions.viewMaintenances,
    globalFilterOptions.viewMaintenancesStatus,
    globalFilterOptions.viewPartPicking,
    globalFilterOptions.viewPartConstraints,
    close,
    trackableSetGlobalFilterOptions,
  ]);

  const queryFilters = useMemo<QueryFilters>(
    () => ({
      deployments: makeQueryFilterIn({ items: globalFilterOptions.deployments, filterKey: "deployment_id" }),
      users: makeQueryFilterIn({ items: globalFilterOptions.users, filterKey: "assignee_id" }),
      manufacturers: makeQueryFilterIn({ items: globalFilterOptions.manufacturers, filterKey: "manufacturer_id" }),
      deviceModels: makeQueryFilterIn({ items: globalFilterOptions.deviceModels, filterKey: "model_id" }),
      devices: makeQueryFilterIn({ items: globalFilterOptions.devices, filterKey: "device_id" }),
      maintenanceStatus: makeQueryFilterIn({ items: globalFilterOptions.maintenanceStatus, filterKey: "status" }),
      deviceTags: makeQueryFilterIn({ items: globalFilterOptions.deviceTags, filterKey: "device_tags.id" }),
      caseTags: makeQueryFilterIn({ items: globalFilterOptions.caseTags, filterKey: "case_tags.id" }),
      caseTypes: makeQueryFilterIn({ items: globalFilterOptions.caseTypes, filterKey: "type" }),
      caseStatus: makeQueryFilterIn({ items: globalFilterOptions.caseStatus, filterKey: "status" }),
      maintenanceTags: makeQueryFilterIn({
        items: globalFilterOptions.maintenanceTags,
        filterKey: "maintenance_tags.id",
      }),
    }),
    [globalFilterOptions],
  );

  const optionsApplied = Object.keys(globalFilterOptions)
    .map((filterKey) => {
      const _fk = filterKey as keyof typeof globalFilterOptions;
      if (
        [
          "mDateFrom",
          "mDateUntil",
          "mDate",
          "viewDaily",
          "viewServiceJobs",
          "viewServiceJobsGantt",
          "viewCases",
          "viewCasesStatus",
          "viewMaintenances",
          "viewMaintenancesStatus",
          "viewPartPicking",
          "viewPartConstraints",
        ].includes(_fk)
      ) {
        return false;
      } else {
        return (globalFilterOptions[_fk]?.length ?? 0) > 0;
      }
    })
    .some((keys) => keys);

  useLayoutEffect(() => {
    // ! ensure we're only messing with search params, when user is authenticated (see Layout/LayoutDeterminer), otherwise breaks Auth Redirect Search Params
    if (isAuthenticated && !deepEqual(globalFilterOptions, filterOptions)) {
      void navigate({
        replace: true,
        // @ts-expect-error don't know whats wrong
        search: (prev) => ({
          ...prev,
          ...globalFilterOptions,
        }),
      });
    }
  }, [globalFilterOptions, filterOptions, navigate, isAuthenticated]);

  const value = useMemo(
    () => ({
      expanded,
      optionsApplied,
      toggle,
      close,
      options: globalFilterOptions,
      setOptions: trackableSetGlobalFilterOptions,
      resetOptions,
      queryFilters,
    }),
    [
      expanded,
      globalFilterOptions,
      optionsApplied,
      queryFilters,
      resetOptions,
      toggle,
      close,
      trackableSetGlobalFilterOptions,
    ],
  );

  return <GlobalFiltersContext value={value}>{children}</GlobalFiltersContext>;
};

export default GlobalFiltersProvider;
